import type { EsnTourPointer } from '@echospaces-web-monorepo/es-ui-next';

/**
 * One stop of the guided tour (Figma "Tutorial screens", 2464:17238).
 */
export interface PortalTourStep {
  readonly heading: string;
  readonly body: string;

  /**
   * `data-tour` value of the element this step spotlights; without one (or while that element
   * is not on screen) the callout sits centred on the dimmed screen.
   */
  readonly anchor?: string;

  /**
   * Callout edge with the arrow; `none` places an anchored callout below its target.
   */
  readonly pointer?: EsnTourPointer;

  /**
   * Screen the step is explained on; the tour navigates there when the step opens.
   */
  readonly route?: string;
}

/**
 * The `data-tour` anchors the tour looks for. The shell carries `nav` and `help-center`;
 * the feature pages carry the rest.
 */
export const PORTAL_TOUR_ANCHORS = {
  nav: 'nav',
  createIsland: 'create-island',
  islandCard: 'island-card',
  uploadContent: 'upload-content',
  linkSlots: 'link-slots',
  helpCenter: 'help-center',
} as const;

/**
 * Where "Create your first island" on the last step leads.
 */
export const PORTAL_TOUR_FINISH_ROUTE = '/islands';

/**
 * The eight steps, copy from the Figma tour callouts.
 */
export const PORTAL_TOUR_STEPS: readonly PortalTourStep[] = [
  {
    heading: 'Welcome to EchoSpaces',
    body:
      'An island is a place people walk through, and it starts out empty. Three moves fill it: build the space, ' +
      'upload what goes inside it, link one to the other. This takes about a minute.',
  },
  {
    heading: 'One job each',
    body:
      'Islands are the spaces themselves. Content is everything you can show inside them. Link Content is where ' +
      'the two meet. And Client accounts, under Administration, is who else holds a key.',
    anchor: PORTAL_TOUR_ANCHORS.nav,
  },
  {
    heading: 'Start with an island',
    body:
      'Give it a name and it exists — as a Draft. Nothing you make here is visible to anyone until you publish it, ' +
      'so there is no wrong first move.',
    anchor: PORTAL_TOUR_ANCHORS.createIsland,
    route: '/islands',
  },
  {
    heading: 'Each island carries its own state',
    body:
      'The badge says whether it is a Draft, Published or Archived. Its menu holds the rest: publish, link content, ' +
      'archive, delete.',
    anchor: PORTAL_TOUR_ANCHORS.islandCard,
    route: '/islands',
  },
  {
    heading: 'Content lives in one library',
    body:
      'Upload a video, an image or a link once, here. The same asset can sit on a dozen islands at the same time, ' +
      'and you change it in one place.',
    anchor: PORTAL_TOUR_ANCHORS.uploadContent,
    route: '/content',
  },
  {
    heading: 'An island is a set of empty slots',
    body:
      'Every screen, wall and surface inside it is a placeholder waiting for something. Pick one on the left, ' +
      'choose content on the right, and it is placed.',
    anchor: PORTAL_TOUR_ANCHORS.linkSlots,
    pointer: 'left',
    route: '/placeholders-links',
  },
  {
    heading: 'When it does not go that way',
    body:
      'Help Center has the answers to what this console gets asked, the way through to a person, and the form ' +
      'that tells us what is missing. It sits at the foot of the menu, under the work.',
    anchor: PORTAL_TOUR_ANCHORS.helpCenter,
    pointer: 'bottom',
  },
  {
    heading: 'That is the whole loop',
    body:
      'Build, upload, link, publish. You can come back to this walkthrough at any time from Take the tour, ' +
      'directly under Help Center.',
  },
];
